import { useState, useEffect } from 'react';
import { getfotametrics, getmessagestrendmetrics, getvnctrendmetrics, getraymondvnctrendmetrics, getBSOCMetrics, getraymondfotametrics, getraymondmessagestrenddrilldown } from '../../../service/telemetricDashboardService';
import { buildFilterObject, buildMessagesTrendFilterObject } from '../utils/filterUtils';

// Custom hook for FOTA metrics
export const useFotaMetrics = (currentManufacturer, chartFilters = {}) => {
  const [fotametricsdata, setFotametricsdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFotaMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = currentManufacturer === 'toyota' ? await getfotametrics() : await getraymondfotametrics();
        setFotametricsdata(response?.data || []);
      } catch (error) {
        console.error('Error fetching FOTA metrics:', error);
        setError(error);
        setFotametricsdata([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFotaMetrics();
  }, [currentManufacturer, JSON.stringify(chartFilters)]); // Re-fetch when manufacturer or filters change

  return { fotametricsdata, loading, error };
};

// Custom hook for Messages Trend metrics
export const useMessagesTrendMetrics = (currentManufacturer, chartFilters = {}, isDrilldownActive) => {
  const [messagesTrendData, setMessagesTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDrilldownData, setIsDrilldownData] = useState(false);

  useEffect(() => {
    const fetchMessagesTrendMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const manufacturer = currentManufacturer; //?.startsWith('raymond') ? 'raymond' : 'toyota';
        const filters = buildMessagesTrendFilterObject(chartFilters);
        const response = isDrilldownActive ?
          await getraymondmessagestrenddrilldown(manufacturer, filters) :
          await getmessagestrendmetrics(manufacturer, filters);
        setMessagesTrendData(response?.data || []);
        setIsDrilldownData(isDrilldownActive);
      } catch (error) {
        console.error('Error fetching messages trend metrics:', error);
        setError(error);
        setMessagesTrendData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessagesTrendMetrics();
  }, [currentManufacturer, chartFilters.siteids, chartFilters.vehiclemodels, chartFilters.vehicletypes, isDrilldownActive]); // Only re-fetch when backend filters change (exclude direction and messagetypes)

  return { messagesTrendData, loading, error, isDrilldownData };
};

// Custom hook for VNC Trend metrics
export const useVncTrendMetrics = (currentManufacturer, chartFilters = {}) => {
  const [vncTrendData, setVncTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVncTrendMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const filters = buildFilterObject(chartFilters);
        
        if (currentManufacturer === 'toyota') {
          const response = await getvnctrendmetrics(filters);
          setVncTrendData(response?.data || []);
        } else if (currentManufacturer.startsWith('raymond')) {
          const response = await getraymondvnctrendmetrics(filters, currentManufacturer);
          setVncTrendData(response?.data || []);
        } else {
          setVncTrendData([]);
        }
      } catch (error) {
        console.error('Error fetching VNC trend metrics:', error);
        setError(error);
        setVncTrendData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVncTrendMetrics();
  }, [currentManufacturer, chartFilters.siteids, chartFilters.vehiclemodels, chartFilters.vehicletypes]); // Only watch relevant filters

  return { vncTrendData, loading, error };
};

// Custom hook for BSOC metrics (Toyota only)
export const useBSOCMetrics = (currentManufacturer, chartFilters = {}) => {
  const [bsocData, setBSOCData] = useState({ min: null, max: null, avg: null, unit: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBSOCMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Only fetch for Toyota
        if (currentManufacturer === 'toyota') {
          const filters = buildFilterObject(chartFilters);
          const response = await getBSOCMetrics(filters);
          
          // Use pre-calculated values from Druid query
          if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
            const result = response.data[0];
            setBSOCData({
              min: result.MinBSOC !== null && result.MinBSOC !== undefined ? result.MinBSOC.toFixed(1) : null,
              max: result.MaxBSOC !== null && result.MaxBSOC !== undefined ? result.MaxBSOC.toFixed(1) : null,
              avg: result.AvgBSOC !== null && result.AvgBSOC !== undefined ? result.AvgBSOC.toFixed(1) : null,
              unit: result.Unit || '%'
            });
          } else {
            setBSOCData({ min: null, max: null, avg: null, unit: '%' });
          }
        } else {
          setBSOCData({ min: null, max: null, avg: null, unit: '%' });
        }
      } catch (error) {
        console.error('Error fetching BSOC metrics:', error);
        setError(error);
        setBSOCData({ min: null, max: null, avg: null, unit: '%' });
      } finally {
        setLoading(false);
      }
    };

    fetchBSOCMetrics();
  }, [currentManufacturer, chartFilters.siteids, chartFilters.vehiclemodels, chartFilters.vehicletypes]);

  return { bsocData, loading, error };
};
